// src/components/ReportStyleModal.jsx — the report style chooser.
// ---------------------------------------------------------------------
// v0.103.0 — Best-in-class style picker for the defect report PDF.
// Shows all six layout styles as live miniature page mocks (cover +
// body, rendered in HTML/CSS with three sample defects) so the user
// SEES each style before applying it — not just named radio buttons.
// A large preview pane tracks hover/selection, and the accent colour
// (curated swatches + free colour input) re-tints every preview live.
//
// App chrome uses theme tokens (suite rule). The mock pages INSIDE the
// previews depict PDF paper, so literal neutrals + the chosen accent
// hex are correct there — same convention as ReportScreen's old
// ThemeSwatch.
//
// Persistence is the caller's job (ReportScreen): applied choices go
// into report settings (saved to the Report row's settingsJson when
// bound) and into the droppin.reportStyle.v1 localStorage preference.

import React, { useState } from 'react';
import { theme, card, btnPrimary, btnSecondary } from '../theme';
import { Palette, Check } from 'lucide-react';
import { REPORT_STYLES, REPORT_STYLE_ORDER, CURATED_COLORS } from '../utils/report-styles';
import { isProTier } from '../utils/tier';

// Sample defects that populate every mock page. Same data in every
// style — that's the whole point.
const SAMPLE = [
  { num: 12, desc: 'Cracked tile to ensuite floor near shower hob', status: 'open',      st: '#dc2626', prio: 'High' },
  { num: 13, desc: 'Paint touch-up to hallway bulkhead',            status: 'rectified', st: '#d97706', prio: 'Med'  },
  { num: 14, desc: 'Door seal missing to laundry external door',    status: 'complete',  st: '#15803d', prio: 'Low'  },
];

const PAPER = '#ffffff';
const INK = '#1f2937';
const MUTED = '#94a3b8';
const PHOTO = 'linear-gradient(135deg,#cbd5e1,#94a3b8)';

export default function ReportStyleModal({ initialStyleKey, initialColor, onApply, onClose }) {
  const [styleKey, setStyleKey] = useState(initialStyleKey || 'signature');
  // Colour is only persisted once the user actually touches it —
  // applying just a style must NOT stamp a colour over a Pro tenant's
  // configured brand colour. Untouched = null = keep current brand.
  const [color, setColorRaw] = useState(initialColor || '#fb923c');
  const [touchedColor, setTouchedColor] = useState(!!initialColor);
  const pro = isProTier();   // v0.117.0 — colour branding is Pro-only (styles stay free)
  const setColor = (v) => { setColorRaw(v); setTouchedColor(true); };
  const [hoverKey, setHoverKey] = useState(null);

  const previewKey = hoverKey || styleKey;
  const preview = REPORT_STYLES[previewKey] || REPORT_STYLES.signature;

  const validHex = /^#[0-9a-fA-F]{6}$/.test(color);
  const accent = validHex ? color : '#fb923c';

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ ...card, width: '100%', maxWidth: 1020, maxHeight: '92vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 12px', borderBottom: `1px solid ${theme.border}` }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Palette size={16} /> Report style
            </h2>
            <div style={{ fontSize: 12, color: theme.textLight, marginTop: 2 }}>
              Same data in every style — only the presentation changes. Previews re-tint as you pick a colour.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: theme.textLight, padding: 0, lineHeight: 1 }} title="Close">×</button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', gap: 16, padding: 16, overflow: 'hidden', flex: 1, minHeight: 0, flexWrap: 'wrap' }}>
          {/* Style grid */}
          <div style={{ flex: '1 1 420px', overflowY: 'auto', minHeight: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
              {REPORT_STYLE_ORDER.map(key => {
                const s = REPORT_STYLES[key];
                const active = styleKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStyleKey(key)}
                    onMouseEnter={() => setHoverKey(key)}
                    onMouseLeave={() => setHoverKey(h => (h === key ? null : h))}
                    style={{
                      position: 'relative', textAlign: 'left', cursor: 'pointer',
                      fontFamily: 'inherit', padding: 8,
                      border: `2px solid ${active ? theme.brand : theme.border}`,
                      borderRadius: 10,
                      background: active ? theme.bg : theme.surface,
                      display: 'flex', flexDirection: 'column', gap: 6,
                    }}
                  >
                    {active && (
                      <span style={{
                        position: 'absolute', top: 6, right: 6, zIndex: 2,
                        width: 20, height: 20, borderRadius: '50%',
                        background: theme.brand, color: 'white',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}><Check size={12} /></span>
                    )}
                    <MiniPage styleKey={key} accent={accent} page="body" width={150} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>
                        {s.name}{key === 'signature' && <span style={{ fontSize: 10, fontWeight: 600, color: theme.textLight }}> · default</span>}
                      </div>
                      <div style={{ fontSize: 10.5, color: theme.textLight, lineHeight: 1.35 }}>{s.blurb}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Large preview + colour */}
          <div style={{ flex: '0 1 330px', minWidth: 280, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Preview — {preview.name}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div>
                  <MiniPage styleKey={previewKey} accent={accent} page="cover" width={150} />
                  <div style={{ fontSize: 10, color: theme.textLight, textAlign: 'center', marginTop: 3 }}>Cover</div>
                </div>
                <div>
                  <MiniPage styleKey={previewKey} accent={accent} page="body" width={150} />
                  <div style={{ fontSize: 10, color: theme.textLight, textAlign: 'center', marginTop: 3 }}>Defects</div>
                </div>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Accent colour
                {/* v0.117.0 — Brett: accent colour is PRO-ONLY. Styles stay free;
                    Free tier sees the controls disabled with an upgrade hint and
                    the previews keep the default accent. */}
                {!pro && (
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: theme.brand, border: `1px solid ${theme.brand}`, borderRadius: 5, padding: '1px 6px', letterSpacing: '0.06em' }}>PRO</span>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, opacity: pro ? 1 : 0.45, pointerEvents: pro ? 'auto' : 'none' }}>
                {CURATED_COLORS.map(c => {
                  const on = accent.toLowerCase() === c.hex.toLowerCase();
                  return (
                    <button
                      key={c.hex}
                      type="button"
                      title={c.label}
                      disabled={!pro}
                      onClick={() => setColor(c.hex)}
                      style={{
                        width: 28, height: 28, borderRadius: 8, cursor: pro ? 'pointer' : 'default',
                        background: c.hex,
                        border: on ? `2px solid ${theme.text}` : `1px solid ${theme.border}`,
                        outline: on ? `2px solid ${theme.surface}` : 'none',
                        outlineOffset: -4,
                        padding: 0,
                      }}
                    />
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: pro ? 1 : 0.45 }}>
                <input
                  type="color"
                  value={accent}
                  disabled={!pro}
                  onChange={e => setColor(e.target.value)}
                  title={pro ? 'Pick any colour' : 'Custom report colour is a Pro feature'}
                  style={{ width: 34, height: 30, padding: 0, border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.surface, cursor: pro ? 'pointer' : 'default' }}
                />
                <input
                  value={color}
                  disabled={!pro}
                  onChange={e => setColor(e.target.value.startsWith('#') ? e.target.value : '#' + e.target.value)}
                  maxLength={7}
                  spellCheck={false}
                  style={{
                    width: 90, fontSize: 13, padding: '6px 10px', fontFamily: 'ui-monospace, monospace',
                    border: `1px solid ${validHex ? theme.border : theme.danger}`, borderRadius: 6,
                    background: theme.surface, color: theme.text, outline: 'none', boxSizing: 'border-box',
                  }}
                />
                <span style={{ fontSize: 11, color: theme.textLight }}>
                  {pro ? (validHex ? 'Used for accents on the PDF.' : 'Enter a 6-digit hex colour.')
                       : 'Report colour branding is available on Pro.'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 20px', borderTop: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 11, color: theme.textLight }}>
            Your choice sticks for future reports; saved reports remember their own style.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={btnSecondary}>Cancel</button>
            <button
              onClick={() => onApply({ styleKey, brandColor: (pro && touchedColor && validHex) ? color : null })}
              style={btnPrimary}
            >
              Apply style
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// MiniPage — a scaled-down mock report page. Designed at 200px wide
// (283px tall = A4 ratio) and scaled to `width` with a CSS transform,
// so every internal measurement is a plain design-space pixel.
function MiniPage({ styleKey, accent, page, width = 150 }) {
  const W = 200, H = 283;
  const scale = width / W;
  return (
    <div style={{ width, height: H * scale, overflow: 'hidden', borderRadius: 4, border: `1px solid ${theme.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
      <div style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: 'top left', background: PAPER, position: 'relative', fontFamily: 'Helvetica, Arial, sans-serif' }}>
        {page === 'cover'
          ? <MockCover styleKey={styleKey} accent={accent} />
          : <MockBody styleKey={styleKey} accent={accent} />}
      </div>
    </div>
  );
}

const serif = 'Georgia, "Times New Roman", serif';

// ---- cover mocks ------------------------------------------------------
function MockCover({ styleKey, accent }) {
  const kpis = ['3', '1', '1', '1', '67%'];
  const kpiLabels = ['TOTAL', 'OPEN', 'RECT', 'COMP', 'DONE'];

  if (styleKey === 'signature') {
    return (
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,#0f172a 0%,#1e293b 55%,#2d2d2d 100%)', color: '#fff' }}>
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, background: accent }} />
        <div style={{ position: 'absolute', left: 14, top: 18, fontWeight: 800, fontSize: 13 }}>DropP<span style={{ color: accent }}>i</span>n</div>
        <div style={{ position: 'absolute', left: 14, top: 34, fontSize: 4.5, letterSpacing: 0.5, color: '#c8c8c8' }}>DROP A PLAN. DROP A PIN. SEND A REPORT.</div>
        <div style={{ position: 'absolute', left: 14, top: 100, fontSize: 7, fontWeight: 800, color: accent, letterSpacing: 0.8 }}>DEFECT REPORT</div>
        <div style={{ position: 'absolute', left: 14, top: 110, fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap' }}>Lot 12 Seaside Drive</div>
        <div style={{ position: 'absolute', left: 14, top: 140, background: 'rgba(255,255,255,0.1)', borderRadius: 3, padding: '3px 8px', fontSize: 6 }}>#241 • Project • 24 Jul 2026</div>
        <div style={{ position: 'absolute', left: 14, right: 20, top: 160, display: 'flex', gap: 3 }}>
          {kpis.map((k, i) => (
            <div key={i} style={{ flex: 1, background: 'rgba(255,255,255,0.08)', borderRadius: 3, padding: '4px 4px' }}>
              <div style={{ fontSize: 9, fontWeight: 800 }}>{k}</div>
              <div style={{ fontSize: 4, color: '#c8c8c8' }}>{kpiLabels[i]}</div>
            </div>
          ))}
        </div>
        <MockCoverMeta color="#e2e8f0" mutedColor="#94a3b8" line="rgba(255,255,255,0.25)" />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 14, background: 'rgba(0,0,0,0.35)' }} />
      </div>
    );
  }

  if (styleKey === 'professional') {
    return (
      <div style={{ position: 'absolute', inset: 0, background: '#fdfbf7', color: '#0f172a', fontFamily: serif }}>
        <div style={{ position: 'absolute', left: 14, top: 18, fontWeight: 800, fontSize: 13, fontFamily: 'Helvetica, Arial, sans-serif' }}>DropP<span style={{ color: accent }}>i</span>n</div>
        <div style={{ position: 'absolute', left: 14, top: 100, fontSize: 7, fontWeight: 700, color: accent, letterSpacing: 1 }}>DEFECT REPORT</div>
        <div style={{ position: 'absolute', left: 14, right: 20, top: 108, height: 1, background: accent }} />
        <div style={{ position: 'absolute', left: 14, top: 116, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>Lot 12 Seaside Drive</div>
        <div style={{ position: 'absolute', left: 14, top: 146, fontSize: 6, color: '#64748b' }}>#241  •  Project  •  24 Jul 2026</div>
        <div style={{ position: 'absolute', left: 14, right: 20, top: 160, display: 'flex', gap: 8 }}>
          {kpis.map((k, i) => (
            <div key={i} style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700 }}>{k}</div>
              <div style={{ fontSize: 4, color: '#64748b', fontFamily: 'Helvetica, Arial, sans-serif' }}>{kpiLabels[i]}</div>
            </div>
          ))}
        </div>
        <MockCoverMeta color="#0f172a" mutedColor="#64748b" line="#cbd5e1" />
        <div style={{ position: 'absolute', left: 14, right: 14, bottom: 14, height: 1, background: '#cbd5e1' }} />
      </div>
    );
  }

  if (styleKey === 'bold') {
    return (
      <div style={{ position: 'absolute', inset: 0, background: '#ffffff', color: '#0f172a' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 85, background: accent }} />
        <div style={{ position: 'absolute', left: 14, top: 18, fontWeight: 800, fontSize: 13, color: '#fff' }}>DropP<span style={{ color: '#0f172a' }}>i</span>n</div>
        <div style={{ position: 'absolute', left: 14, top: 34, fontSize: 4.5, letterSpacing: 0.5, color: 'rgba(255,255,255,0.85)' }}>DROP A PLAN. DROP A PIN. SEND A REPORT.</div>
        <div style={{ position: 'absolute', left: 14, top: 100, fontSize: 7, fontWeight: 800, color: accent, letterSpacing: 0.8 }}>DEFECT REPORT</div>
        <div style={{ position: 'absolute', left: 14, width: 28, top: 108, height: 3, background: accent }} />
        <div style={{ position: 'absolute', left: 14, top: 114, fontSize: 17, fontWeight: 900, whiteSpace: 'nowrap' }}>Lot 12 Seaside Drive</div>
        <div style={{ position: 'absolute', left: 14, top: 148, background: '#f1f5f9', borderRadius: 3, padding: '3px 8px', fontSize: 6 }}>#241 • Project • 24 Jul 2026</div>
        <div style={{ position: 'absolute', left: 14, right: 20, top: 166, display: 'flex', gap: 6 }}>
          {kpis.map((k, i) => (
            <div key={i} style={{ flex: 1, borderLeft: `2.5px solid ${accent}`, paddingLeft: 3 }}>
              <div style={{ fontSize: 10, fontWeight: 800 }}>{k}</div>
              <div style={{ fontSize: 4, color: '#64748b' }}>{kpiLabels[i]}</div>
            </div>
          ))}
        </div>
        <MockCoverMeta color="#0f172a" mutedColor="#64748b" line="#cbd5e1" />
      </div>
    );
  }

  if (styleKey === 'compact') {
    return (
      <div style={{ position: 'absolute', inset: 0, background: '#ffffff', color: '#0f172a' }}>
        <div style={{ position: 'absolute', left: 14, top: 18, fontWeight: 800, fontSize: 11 }}>DropP<span style={{ color: accent }}>i</span>n</div>
        <div style={{ position: 'absolute', left: 14, top: 100, fontSize: 6, fontWeight: 800, color: accent, letterSpacing: 0.8 }}>DEFECT REPORT</div>
        <div style={{ position: 'absolute', left: 14, width: 28, top: 107, height: 1.6, background: accent }} />
        <div style={{ position: 'absolute', left: 14, top: 112, fontSize: 12, fontWeight: 700 }}>Lot 12 Seaside Drive</div>
        <div style={{ position: 'absolute', left: 14, top: 130, border: '0.8px solid #0f172a', borderRadius: 3, padding: '2px 7px', fontSize: 5.5 }}>#241 • Project • 24 Jul 2026</div>
        <div style={{ position: 'absolute', left: 14, right: 20, top: 150, display: 'flex', gap: 8 }}>
          {kpis.map((k, i) => (
            <div key={i} style={{ flex: 1 }}>
              <div style={{ fontSize: 8.5, fontWeight: 800 }}>{k}</div>
              <div style={{ fontSize: 4, color: '#64748b' }}>{kpiLabels[i]}</div>
            </div>
          ))}
        </div>
        <MockCoverMeta color="#0f172a" mutedColor="#64748b" line="#e2e8f0" />
      </div>
    );
  }

  if (styleKey === 'modern') {
    return (
      <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', color: '#0f172a' }}>
        <div style={{ position: 'absolute', left: 14, top: 18, fontWeight: 800, fontSize: 13 }}>DropP<span style={{ color: accent }}>i</span>n</div>
        <div style={{ position: 'absolute', left: 14, top: 98, background: accent, color: '#fff', borderRadius: 8, padding: '2.5px 8px', fontSize: 6, fontWeight: 800, letterSpacing: 0.6 }}>DEFECT REPORT</div>
        <div style={{ position: 'absolute', left: 14, top: 115, fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' }}>Lot 12 Seaside Drive</div>
        <div style={{ position: 'absolute', left: 14, top: 144, background: '#f1f5f9', borderRadius: 3, padding: '3px 8px', fontSize: 6 }}>#241 • Project • 24 Jul 2026</div>
        <div style={{ position: 'absolute', left: 14, right: 20, top: 162, display: 'flex', gap: 4 }}>
          {kpis.map((k, i) => (
            <div key={i} style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 4px' }}>
              <div style={{ fontSize: 9, fontWeight: 800 }}>{k}</div>
              <div style={{ fontSize: 4, color: '#64748b' }}>{kpiLabels[i]}</div>
            </div>
          ))}
        </div>
        <MockCoverMeta color="#0f172a" mutedColor="#64748b" line="#e2e8f0" />
      </div>
    );
  }

  // classic
  return (
    <div style={{ position: 'absolute', inset: 0, background: '#ffffff', color: '#0a0a0a', fontFamily: serif, textAlign: 'center' }}>
      <div style={{ position: 'absolute', left: 14, top: 18, fontWeight: 800, fontSize: 12, fontFamily: 'Helvetica, Arial, sans-serif', textAlign: 'left' }}>DropP<span style={{ color: accent }}>i</span>n</div>
      <div style={{ position: 'absolute', left: 14, right: 14, top: 92, height: 2, background: '#0a0a0a' }} />
      <div style={{ position: 'absolute', left: 14, right: 14, top: 96, height: 0.7, background: '#0a0a0a' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: 104, fontSize: 6.5, fontWeight: 700, color: accent, letterSpacing: 1.2 }}>DEFECT REPORT</div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 114, fontSize: 15, fontWeight: 700 }}>Lot 12 Seaside Drive</div>
      <div style={{ position: 'absolute', left: 14, right: 14, top: 138, height: 0.7, background: '#0a0a0a' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: 145, fontSize: 6, color: '#737373' }}>#241  •  Project  •  24 Jul 2026</div>
      <div style={{ position: 'absolute', left: 14, right: 20, top: 162, display: 'flex', gap: 8, textAlign: 'left' }}>
        {kpis.map((k, i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700 }}>{k}</div>
            <div style={{ fontSize: 4, color: '#737373', fontFamily: 'Helvetica, Arial, sans-serif' }}>{kpiLabels[i]}</div>
          </div>
        ))}
      </div>
      <MockCoverMeta color="#0a0a0a" mutedColor="#737373" line="#c8c8c8" />
    </div>
  );
}

// Shared meta block at the bottom of every mock cover.
function MockCoverMeta({ color, mutedColor, line }) {
  return (
    <div style={{ position: 'absolute', left: 14, right: 20, bottom: 26, textAlign: 'left' }}>
      <div style={{ height: 0.7, background: line, marginBottom: 5 }} />
      <div style={{ display: 'flex', gap: 14 }}>
        {[['CUSTOMER', 'J. Citizen'], ['LOCATION', '12 Seaside Dr'], ['STATUS', 'Active'], ['REPORTED', '24 Jul 2026']].map(m => (
          <div key={m[0]}>
            <div style={{ fontSize: 3.6, fontWeight: 800, color: mutedColor, letterSpacing: 0.4 }}>{m[0]}</div>
            <div style={{ fontSize: 5.4, color, marginTop: 1 }}>{m[1]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- body-page mocks ---------------------------------------------------
function MockBody({ styleKey, accent }) {
  return (
    <div style={{ position: 'absolute', inset: 0, padding: '12px 12px 10px', boxSizing: 'border-box', color: INK }}>
      <MockHeader styleKey={styleKey} accent={accent} />
      <div style={{ marginTop: 8 }}>
        {styleKey === 'signature'   && <SigItems accent={accent} />}
        {styleKey === 'professional' && <AiryItems accent={accent} />}
        {styleKey === 'bold'        && <BoldItems accent={accent} />}
        {styleKey === 'compact'     && <RowItems accent={accent} />}
        {styleKey === 'modern'      && <ModernItems accent={accent} />}
        {styleKey === 'classic'     && <RuledItems accent={accent} />}
      </div>
    </div>
  );
}

function MockHeader({ styleKey, accent }) {
  const isSerif = styleKey === 'professional' || styleKey === 'classic';
  const compact = styleKey === 'compact';
  const rule = styleKey === 'professional' || styleKey === 'compact'
    ? { height: 0.8, background: '#cbd5e1' }
    : styleKey === 'classic'
      ? { height: 0.8, background: '#1f2937', boxShadow: '0 1.6px 0 #1f2937' }
      : { height: styleKey === 'bold' ? 3 : 1.6, background: accent };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <span style={{ fontWeight: 800, fontSize: compact ? 8 : 10, color: accent }}>DropP<span style={{ color: '#1d4ed8' }}>i</span>n</span>
          <span style={{ fontSize: 4.5, color: accent, fontWeight: 700, marginLeft: 4, letterSpacing: 0.5 }}>DEFECT REPORT</span>
        </div>
        <span style={{ fontSize: 5.5, color: MUTED }}>#241 · 24 Jul 2026</span>
      </div>
      <div style={{ fontSize: compact ? 6 : 7, fontWeight: 700, marginTop: 1.5, fontFamily: isSerif ? serif : undefined }}>Defect Report — Lot 12 Seaside Drive</div>
      <div style={{ marginTop: 3, ...rule }} />
    </div>
  );
}

const photoBox = (w, h, r = 1) => ({ width: w, height: h, background: PHOTO, borderRadius: r, flexShrink: 0 });

// Signature — the current bordered cards.
function SigItems({ accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {SAMPLE.map(d => (
        <div key={d.num} style={{ border: '0.8px solid #e5e7eb', borderRadius: 3, padding: 4, display: 'flex', gap: 4, height: 56, boxSizing: 'border-box' }}>
          <div style={{ ...photoBox(40, '100%'), background: '#eef2f7', border: '0.6px solid #e2e8f0', position: 'relative' }}>
            <span style={{ position: 'absolute', left: '55%', top: '40%', width: 7, height: 7, borderRadius: '50%', background: accent, color: '#fff', fontSize: 4.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{d.num}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: accent, color: '#fff', fontSize: 4.5, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{d.num}</span>
              <span style={{ fontSize: 4.5, fontWeight: 700, background: `${d.st}22`, color: d.st, borderRadius: 4, padding: '0.5px 3px' }}>{d.status}</span>
              <span style={{ fontSize: 4.5, fontWeight: 700, background: '#f3f4f6', color: '#374151', borderRadius: 4, padding: '0.5px 3px' }}>{d.prio}</span>
            </div>
            <div style={{ fontSize: 5.6, marginTop: 2.5, lineHeight: 1.3 }}>{d.desc}</div>
            <div style={{ fontSize: 4.4, color: MUTED, marginTop: 2 }}>Where: Ensuite · Assigned: BuildCo</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: 34 }}>
            {[0, 1, 2, 3].map(i => <div key={i} style={{ background: PHOTO, borderRadius: 0.5 }} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// Professional — airy serif with hairlines.
function AiryItems({ accent }) {
  return (
    <div>
      {SAMPLE.slice(0, 2).map(d => (
        <div key={d.num} style={{ padding: '6px 0 7px', borderBottom: '0.6px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: serif, fontWeight: 700, fontSize: 7.5 }}>
              No. {d.num} <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontWeight: 400, fontSize: 4.4, color: MUTED, letterSpacing: 0.5 }}> {d.status.toUpperCase()} · {d.prio.toUpperCase()} · TILING</span>
            </div>
            <div style={{ ...photoBox(24, 24, 0), border: '0.6px solid #e2e8f0', background: '#eef2f7', position: 'relative' }}>
              <span style={{ position: 'absolute', left: '50%', top: '42%', width: 6, height: 6, borderRadius: '50%', background: accent }} />
            </div>
          </div>
          <div style={{ fontFamily: serif, fontSize: 6.2, lineHeight: 1.45, marginTop: 2, width: '78%' }}>{d.desc}. Rectify prior to handover inspection.</div>
          <div style={{ fontSize: 4.2, color: MUTED, letterSpacing: 0.4, marginTop: 3 }}>LOCATION: ENSUITE · PLAN: A-101 · ASSIGNED: BUILDCO · RAISED 21 JUL 2026</div>
          <div style={{ display: 'flex', gap: 2.5, marginTop: 3 }}>
            {[0, 1, 2].map(i => <div key={i} style={{ ...photoBox(22, 20, 0), border: '0.6px solid #e2e8f0' }} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// Bold — accent bars + big photos.
function BoldItems({ accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {SAMPLE.slice(0, 2).map(d => (
        <div key={d.num} style={{ display: 'flex', gap: 4 }}>
          <div style={{ width: 3.5, background: accent, borderRadius: 1, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: accent }}>#{d.num}</span>
              <span style={{ fontSize: 4.5, fontWeight: 800, background: d.st, color: '#fff', borderRadius: 1.5, padding: '1px 3.5px' }}>{d.status.toUpperCase()}</span>
              <span style={{ fontSize: 4.5, fontWeight: 700, background: '#f3f4f6', color: '#374151', borderRadius: 4, padding: '0.5px 3px' }}>{d.prio.toUpperCase()}</span>
            </div>
            <div style={{ fontSize: 6.6, fontWeight: 600, marginTop: 2.5, width: '80%' }}>{d.desc}</div>
            <div style={{ fontSize: 4.4, color: MUTED, marginTop: 1.5 }}>Ensuite · Plan A-101 · BuildCo · Raised 21 Jul 2026</div>
            <div style={{ display: 'flex', gap: 3, marginTop: 3 }}>
              <div style={{ ...photoBox('49%', 38, 0) }} />
              <div style={{ ...photoBox('49%', 38, 0) }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Compact — dense striped table rows.
function RowItems({ accent }) {
  const rows = [...SAMPLE, ...SAMPLE.map(d => ({ ...d, num: d.num + 3 })), ...SAMPLE.map(d => ({ ...d, num: d.num + 6 }))];
  return (
    <div>
      {rows.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2.5px 1px', background: i % 2 === 0 ? '#f9fafb' : 'transparent', borderBottom: '0.4px solid #e5e7eb' }}>
          <div style={{ width: 1.5, alignSelf: 'stretch', background: accent }} />
          <span style={{ fontSize: 5.5, fontWeight: 800, width: 8 }}>{d.num}</span>
          <div style={{ ...photoBox(13, 11, 0) }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.desc}</div>
            <div style={{ fontSize: 3.8, color: MUTED }}>Ensuite · Tiling · BuildCo</div>
          </div>
          <span style={{ fontSize: 4, fontWeight: 700, background: `${d.st}22`, color: d.st, borderRadius: 4, padding: '0.5px 3px' }}>{d.status}</span>
          <span style={{ fontSize: 4, fontWeight: 700, background: '#f3f4f6', color: '#374151', borderRadius: 4, padding: '0.5px 3px' }}>{d.prio}</span>
          <span style={{ fontSize: 4.2, color: MUTED, width: 14 }}>A-101</span>
        </div>
      ))}
    </div>
  );
}

// Modern — soft cards + dot chips.
function ModernItems({ accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {SAMPLE.map(d => (
        <div key={d.num} style={{ background: '#f8fafc', borderRadius: 5, padding: 5, display: 'flex', gap: 4, height: 54, boxSizing: 'border-box' }}>
          <div style={{ ...photoBox(38, '100%', 2), background: '#eef2f7', border: '0.6px solid #e2e8f0', position: 'relative' }}>
            <span style={{ position: 'absolute', left: '52%', top: '38%', width: 6.5, height: 6.5, borderRadius: '50%', background: accent }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <span style={{ background: accent, color: '#fff', fontSize: 4.6, fontWeight: 800, borderRadius: 1.5, padding: '1px 3px' }}>{d.num}</span>
              {[[d.status, d.st], [d.prio, '#64748b'], ['Tiling', accent]].map(([t, c], i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, fontSize: 4.2, fontWeight: 700, background: '#f1f5f9', border: '0.5px solid #e2e8f0', color: '#334155', borderRadius: 5, padding: '1px 3.5px' }}>
                  <span style={{ width: 2.6, height: 2.6, borderRadius: '50%', background: c }} />{t}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 5.6, marginTop: 3, lineHeight: 1.3 }}>{d.desc}</div>
            <div style={{ fontSize: 4.2, color: MUTED, marginTop: 2 }}>Ensuite · BuildCo · 21 Jul 2026</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: 30 }}>
            {[0, 1, 2, 3].map(i => <div key={i} style={{ background: PHOTO, borderRadius: 1.5 }} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// Classic — formal ruled blocks with label/value fields.
function RuledItems({ accent }) {
  return (
    <div style={{ fontFamily: serif }}>
      {SAMPLE.slice(0, 2).map(d => (
        <div key={d.num} style={{ borderTop: '1px solid #1f2937', padding: '4px 0 5px', marginBottom: 4, borderBottom: '0.4px solid #a3a3a3' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 6, fontWeight: 700, letterSpacing: 0.5 }}>ITEM NO. {d.num}</span>
            <span style={{ fontSize: 5, color: '#525252' }}>Plan reference: A-101</span>
          </div>
          <div style={{ fontSize: 6, lineHeight: 1.4, marginTop: 2 }}>{d.desc}. Rectify prior to handover inspection.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 8px', marginTop: 2.5 }}>
            {[['LOCATION', 'Ensuite'], ['TRADE', 'Tiling'], ['STATUS', d.status], ['PRIORITY', d.prio], ['ASSIGNED', 'BuildCo'], ['RAISED', '21 Jul 2026']].map(f => (
              <div key={f[0]} style={{ display: 'flex', gap: 3, fontSize: 4.6 }}>
                <span style={{ fontWeight: 700, color: '#737373', width: 22 }}>{f[0]}:</span>
                <span style={{ color: '#171717' }}>{f[1]}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 2, marginTop: 3 }}>
            <div style={{ ...photoBox(20, 17, 0), background: '#eef2f7', border: '0.5px solid #a3a3a3', position: 'relative' }}>
              <span style={{ position: 'absolute', left: '50%', top: '40%', width: 5, height: 5, borderRadius: '50%', background: accent }} />
            </div>
            {[0, 1].map(i => <div key={i} style={{ ...photoBox(20, 17, 0), border: '0.5px solid #a3a3a3' }} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
